import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCheckboxField, SpecraFormField, SpecraInput, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { downloadDocx, rfiEvidenceApi } from './api';
/** Kept mounted beside the manual editor: no provider or review failure replaces its buffer. */ export function RfiEvidence({ target, questionId, onQuestionChange, api = rfiEvidenceApi, reviewOfficeDocx }) {
    const options = useQuery({
        queryKey: [
            'rfi-evidence-options',
            target
        ],
        queryFn: ()=>api.options(target),
        retry: false,
        gcTime: 0
    });
    const questions = useQuery({
        queryKey: [
            'rfi-evidence-questions',
            target
        ],
        queryFn: ()=>api.questions(target),
        retry: false,
        gcTime: 0
    });
    const history = useQuery({
        queryKey: [
            'rfi-evidence-history',
            target
        ],
        queryFn: ()=>api.state(target),
        retry: false,
        gcTime: 0,
        refetchInterval: 5000
    });
    const [basis, setBasis] = useState([]);
    const [review, setReview] = useState();
    const [intent, setIntent] = useState();
    const [intentKind, setIntentKind] = useState('draft');
    const [edited, setEdited] = useState();
    const [confirmed, setConfirmed] = useState(false);
    const [followUp, setFollowUp] = useState('');
    const [provenance, setProvenance] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const question = questions.data?.questions.find((q)=>q.id === questionId && q.sectionIds.includes(target.sectionId) && !q.stale);
    const run = async (action)=>{
        setBusy(true);
        setError('');
        setNotice('');
        try {
            await action();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed. Your manual answer is still open.');
        } finally{
            setBusy(false);
        }
    };
    const decide = (choice)=>{
        if (!review) return;
        // Retain the first payload through response loss. The backend freezes intent on claim.
        const payload = intent || review.intent || choice;
        setIntent(payload);
        void run(async ()=>{
            await api.decide(review, payload);
            setReview({
                ...review,
                status: payload.decision === 'rejected' ? 'rejected' : 'accepted',
                intent: payload
            });
            window.dispatchEvent(new Event('shanks-document-applied'));
            await history.refetch();
            setNotice(payload.decision === 'rejected' ? 'Proposal rejected. No answer or citations were inserted.' : 'Reviewed replacement saved. Compare the server version in the manual editor before opening it. Submission is not approved.');
        });
    };
    const reviewHistory = history.data?.history.find((h)=>h.draft?.id === review?.draft.id);
    const withheld = !!reviewHistory?.evidenceWithheld || !!reviewHistory?.sourceWithdrawn || history.isError;
    const retryIntent = intent || review?.intent;
    const terminal = review && [
        'accepted',
        'rejected'
    ].includes(review.status);
    const pending = history.data?.history.some((h)=>h.draft?.provenance === 'framework' && !h.decision);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 rounded-md border border-border p-3",
        "data-testid": Testids.rfiEvidence.panel,
        "aria-label": "RFI evidence and AI review",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                children: "Evidence for this section · manual writing remains available below"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Question for this section",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Question for this section",
                    "data-testid": Testids.rfiEvidence.question,
                    value: questionId,
                    disabled: busy || !!review && !terminal,
                    onChange: (e)=>{
                        setBasis([]);
                        onQuestionChange(e.target.value);
                    },
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Choose a mapped question"
                        }),
                        questions.data?.questions.filter((q)=>q.sectionIds.includes(target.sectionId) && !q.stale).map((q)=>/*#__PURE__*/ _jsx("option", {
                                value: q.id,
                                children: q.source?.originalText || q.suggestion?.interpretation
                            }, q.id))
                    ]
                })
            }),
            (questions.isLoading || options.isLoading || history.isLoading) && /*#__PURE__*/ _jsx(SpecraText, {
                children: "Loading permitted evidence and review history…"
            }),
            (questions.isError || options.isError || history.isError) && /*#__PURE__*/ _jsx(SpecraButton, {
                disabled: busy,
                onClick: ()=>void run(async ()=>{
                        await Promise.all([
                            questions.refetch(),
                            options.refetch(),
                            history.refetch()
                        ]);
                    }),
                children: "Retry evidence and history"
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Permitted Library or artifact evidence",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Permitted Library or artifact evidence",
                    "data-testid": Testids.rfiEvidence.select,
                    value: "",
                    disabled: busy || !question || basis.length >= 8,
                    onChange: (e)=>{
                        const option = options.data?.find((o)=>o.key === e.target.value);
                        if (option) void run(async ()=>{
                            const resolved = await api.resolve(target, questionId, option.source);
                            if (!resolved?.source) throw new Error('Evidence is unavailable. Refresh permitted sources.');
                            setBasis((current)=>current.some((b)=>b.source?.equals(resolved.source)) ? current : [
                                    ...current,
                                    resolved
                                ]);
                        });
                    },
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Add evidence (up to eight)"
                        }),
                        options.data?.map((o)=>/*#__PURE__*/ _jsx("option", {
                                value: o.key,
                                children: o.title
                            }, o.key))
                    ]
                })
            }),
            !withheld && basis.map((b, index)=>/*#__PURE__*/ _jsxs("article", {
                    "data-testid": Testids.rfiEvidence.basis,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            children: [
                                b.title || b.source?.artifactId || b.source?.releaseId,
                                " · ",
                                b.source?.kind,
                                " · version",
                                ' ',
                                b.source?.releaseVersion || b.source?.artifactVersion,
                                " · ",
                                b.reuseStatus
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            children: [
                                b.releaseContext?.externalOutputEligibility || 'External eligibility unverified',
                                " ·",
                                ' ',
                                b.releaseContext?.restrictions,
                                " ",
                                b.releaseContext?.externalOutputRestrictions
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "secondary",
                            disabled: busy,
                            onClick: ()=>setBasis((all)=>all.filter((_, i)=>i !== index)),
                            children: [
                                "Remove evidence ",
                                index + 1
                            ]
                        })
                    ]
                }, `${b.source?.releaseId || b.source?.artifactId}-${index}`)),
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.rfiEvidence.gap,
                children: /*#__PURE__*/ _jsx(SpecraText, {
                    children: review?.draft.materialGaps.length && !withheld ? review.draft.materialGaps.join('\n') : 'Missing or unsubstantiated outcomes and metrics require evidence. Select permitted evidence or request a focused follow-up.'
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Focused Delivery, partner or SME input needed",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    "aria-label": "Focused Delivery, partner or SME input needed",
                    "data-testid": Testids.rfiEvidence.followUp,
                    value: followUp,
                    onChange: (e)=>setFollowUp(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Input provenance (person, team, date, reference)",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Input provenance (person, team, date, reference)",
                    value: provenance,
                    onChange: (e)=>setProvenance(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                children: "Returned manual input is unverified, local to this response and not approved for reuse. Record its origin; a follow-up grants no Program access."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiEvidence.createFollowUp,
                disabled: busy || !followUp.trim() || !provenance.trim(),
                onClick: ()=>void run(async ()=>{
                        await api.followUp(target, `[Question ${questionId || 'unselected'}; provenance: ${provenance}; reuse: unverified/local only] ${followUp}`);
                        setFollowUp('');
                        setNotice('Focused follow-up saved in this response’s Follow-ups.');
                    }),
                children: "Save focused follow-up"
            }),
            /*#__PURE__*/ _jsxs(SpecraSelect, {
                "aria-label": "AI task",
                value: intentKind,
                onChange: (e)=>setIntentKind(e.target.value),
                disabled: busy,
                children: [
                    /*#__PURE__*/ _jsx("option", {
                        value: "draft",
                        children: "Draft"
                    }),
                    /*#__PURE__*/ _jsx("option", {
                        value: "summarize",
                        children: "Summarize"
                    }),
                    /*#__PURE__*/ _jsx("option", {
                        value: "compress",
                        children: "Compress"
                    }),
                    /*#__PURE__*/ _jsx("option", {
                        value: "check",
                        children: "Check"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.rfiEvidence.generate,
                disabled: busy || !question || !basis.length || !!pending || !!review && !terminal || history.isError || history.isLoading,
                onClick: ()=>void run(async ()=>{
                        const result = await api.generate(target, questionId, basis.flatMap((b)=>b.source ? [
                                b.source
                            ] : []), intentKind);
                        setReview(result);
                        setIntent(undefined);
                        setEdited(undefined);
                        setConfirmed(false);
                        await history.refetch();
                    }),
                children: "Generate reviewable proposal"
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                children: "AI uses the saved question and selected authorized sources. Source instructions are untrusted. Generation never approves submission. Acceptance replaces the entire saved section document; unsaved manual work remains in the editor."
            }),
            history.data?.history.map((h)=>/*#__PURE__*/ _jsxs("article", {
                    "data-testid": Testids.rfiEvidence.history,
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            children: [
                                h.decision || 'Pending review',
                                " ·",
                                ' ',
                                h.currentAnswer ? 'Current saved answer' : 'Historical proposal',
                                " ·",
                                ' ',
                                h.outwardEligible ? 'Eligible for outward check' : 'Excluded from outward output'
                            ]
                        }),
                        (h.sourceWithdrawn || h.evidenceWithheld) && /*#__PURE__*/ _jsxs(SpecraText, {
                            "data-testid": Testids.rfiEvidence.withdrawal,
                            children: [
                                h.sourceWithdrawn ? 'Evidence withdrawn.' : 'Evidence access unavailable.',
                                " Derived prose is withheld from outward output. History is retained."
                            ]
                        }),
                        h.draft?.provenance === 'framework' && /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: busy,
                            onClick: ()=>void run(async ()=>{
                                    if (!h.draft) throw new Error('Saved proposal identity is unavailable.');
                                    const restored = await api.restore(h.draft);
                                    setReview(restored);
                                    setIntent(restored.intent);
                                    setEdited(undefined);
                                    setConfirmed(false);
                                }),
                            children: "Reopen saved review"
                        }),
                        h.currentAnswer && /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.rfiEvidence.export,
                            disabled: busy,
                            onClick: ()=>void run(async ()=>{
                                    if (!h.draft) throw new Error('Saved answer identity is unavailable.');
                                    downloadDocx(await api.outward(target, h.draft.id), 'rfi-reviewed-outward.docx');
                                }),
                            children: "Download protected outward DOCX"
                        })
                    ]
                }, h.draft?.id)),
            review && /*#__PURE__*/ _jsxs("article", {
                className: "space-y-3",
                "data-testid": Testids.rfiEvidence.review,
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Saved proposal · ",
                            review.status,
                            " · question:",
                            ' ',
                            review.questionText || 'See the selected mapped question'
                        ]
                    }),
                    /*#__PURE__*/ _jsx("pre", {
                        className: "whitespace-pre-wrap",
                        children: withheld ? 'Evidence unavailable or withdrawn. Derived proposal text is withheld.' : review.draft.proposedText
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Basis:",
                            ' ',
                            (withheld ? [] : review.draft.basis).map((b)=>`${b.kind} ${b.releaseId || b.artifactId} v${b.releaseVersion || b.artifactVersion}`).join('; ')
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            "Material gaps:",
                            ' ',
                            (withheld ? [] : review.draft.materialGaps).join('; ') || 'None reported; verify all claims before accepting.'
                        ]
                    }),
                    review.preview?.length && !withheld ? /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>review.preview && downloadDocx(review.preview, 'rfi-proposal-review.docx'),
                        children: "Download proposal DOCX for Office review/edit"
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Reopened proposal: original acceptance is reconstructed and verified on the server. To edit, supply the complete reviewed DOCX."
                    }),
                    !terminal && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            reviewOfficeDocx && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.rfiEvidence.useOfficeEdit,
                                disabled: busy || !!intent || !!review.intent || withheld,
                                onClick: ()=>void run(async ()=>{
                                        setEdited(undefined);
                                        setConfirmed(false);
                                        const bytes = await reviewOfficeDocx();
                                        if (!bytes.length || bytes.length > 8 * 1024 * 1024) throw new Error('The complete reviewed Office DOCX must be no larger than 8 MiB.');
                                        setEdited(bytes.slice());
                                        setNotice('Current Office DOCX captured without saving. Review the full replacement and confirm before adopting this edit.');
                                    }),
                                children: "Use my current Office document as the reviewed edit"
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Complete DOCX edited and reviewed in Office (maximum 8 MiB)",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    type: "file",
                                    accept: ".docx",
                                    "aria-label": "Complete reviewed DOCX",
                                    "data-testid": Testids.rfiEvidence.editedFile,
                                    disabled: busy || !!intent || !!review.intent,
                                    onChange: (e)=>{
                                        const file = e.target.files?.[0];
                                        setEdited(undefined);
                                        setConfirmed(false);
                                        if (file) void run(async ()=>{
                                            if (file.size > 8 * 1024 * 1024 || !file.size || !file.name.toLowerCase().endsWith('.docx')) throw new Error('Choose a complete DOCX no larger than 8 MiB.');
                                            setEdited(new Uint8Array(await file.arrayBuffer()));
                                        });
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                "data-testid": Testids.rfiEvidence.confirm,
                                label: "I reviewed the full replacement, evidence and gaps. This is not submission approval.",
                                controlProps: {
                                    checked: confirmed,
                                    onCheckedChange: (checked)=>setConfirmed(checked === true)
                                }
                            }),
                            retryIntent ? /*#__PURE__*/ _jsx(SpecraButton, {
                                disabled: busy || withheld && retryIntent.decision !== 'rejected',
                                "data-testid": Testids.rfiEvidence.retry,
                                onClick: ()=>decide(retryIntent),
                                children: "Retry original decision and bytes"
                            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.rfiEvidence.accept,
                                        disabled: busy || !confirmed || withheld,
                                        onClick: ()=>decide({
                                                decision: 'accepted'
                                            }),
                                        children: "Accept full section replacement"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.rfiEvidence.edit,
                                        disabled: busy || !confirmed || !edited || withheld,
                                        onClick: ()=>decide({
                                                decision: 'edited',
                                                reviewedContent: edited
                                            }),
                                        children: "Adopt reviewed DOCX edit"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        "data-testid": Testids.rfiEvidence.reject,
                                        disabled: busy,
                                        onClick: ()=>decide({
                                                decision: 'rejected'
                                            }),
                                        children: "Reject proposal"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            busy && /*#__PURE__*/ _jsx("output", {
                "aria-live": "polite",
                children: "Working… Your manual editor remains open."
            }),
            error && /*#__PURE__*/ _jsxs("output", {
                role: "alert",
                "data-testid": Testids.rfiEvidence.error,
                children: [
                    error,
                    " Your manual answer is retained. Retry or continue writing manually."
                ]
            }),
            notice && /*#__PURE__*/ _jsx("output", {
                "aria-live": "polite",
                children: notice
            })
        ]
    });
}
